import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const err = (e) => e?.response?.data?.detail || e?.message || "Request failed";
const pct = (v) => `${v ?? 0}%`;

function Kpi({ label, value, testId }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div><div className="mt-1 text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{value}</div></div>;
}

function Bar({ v }) {
  return <div className="h-1.5 w-full rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-teal-600 transition-[width] duration-500" style={{ width: `${Math.min(100, v || 0)}%` }} /></div>;
}

function Table({ title, rows, label, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testId}>
      <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800">{title}</div>
      {!rows.length && <div className="p-4 text-xs text-slate-500">No labels in this period yet.</div>}
      {rows.map((r, i) => (
        <div key={`${r.key}-${i}`} className="grid grid-cols-[1fr_70px_70px_70px] items-center gap-3 border-b border-slate-50 px-4 py-2 text-xs last:border-0" data-testid={`${testId}-row-${i}`}>
          <div className="min-w-0"><div className="truncate text-slate-800" title={label(r)}>{label(r)}</div><Bar v={r.scan_rate} /></div>
          <div className="text-right"><div className="font-bold text-slate-900">{pct(r.scan_rate)}</div><div className="text-slate-500">{r.scans} scans</div></div>
          <div className="text-right"><div className="font-bold text-slate-900">{r.avg_rating ?? "—"}</div><div className="text-slate-500">{r.feedback} ratings</div></div>
          <div className="text-right text-slate-600">{r.labels} labels</div>
        </div>))}
    </div>
  );
}

export function TipInsights() {
  const [days, setDays] = useState(90);
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/welcome-label/insights", { params: { days } }).then((r) => setD(r.data)).catch((e) => toast.error(err(e))); }, [days]);
  if (!d) return <div className="text-sm text-slate-500">Loading insights…</div>;
  const T = d.totals;
  return (
    <div className="space-y-4" data-testid="tip-insights">
      <div className="flex flex-wrap items-center gap-2">
        {[30, 90, 365].map((n) => <button key={n} onClick={() => setDays(n)} className={`rounded-full px-3 py-1 text-xs font-semibold ${days === n ? "bg-slate-900 text-white" : "border border-slate-200 bg-white text-slate-700"}`} data-testid={`insights-days-${n}`}>Last {n} days</button>)}
        <span className="ml-auto text-xs text-slate-500">{d.note}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Labels" value={T.labels} testId="kpi-labels" /><Kpi label="QR scan rate" value={pct(T.scan_rate)} testId="kpi-scan-rate" /><Kpi label="Total scans" value={T.scans} testId="kpi-scans" />
        <Kpi label="Feedback rate" value={pct(T.feedback_rate)} testId="kpi-feedback-rate" /><Kpi label="Avg rating" value={T.avg_rating ?? "—"} testId="kpi-avg-rating" />
      </div>
      <Table title="Top tips by scan rate" rows={d.tips} label={(r) => r.tip_text || r.key} testId="insights-tips" />
      <div className="grid gap-4 lg:grid-cols-2">
        <Table title="Headings" rows={d.headings} label={(r) => r.key} testId="insights-headings" />
        <Table title="Content types" rows={d.families} label={(r) => String(r.key).replace(/_/g, " ")} testId="insights-families" />
        <Table title="Languages" rows={d.languages} label={(r) => r.name} testId="insights-languages" />
        <Table title="Customers" rows={d.customers} label={(r) => r.key} testId="insights-customers" />
      </div>
    </div>
  );
}
